import { z } from 'zod';

/**
 * Payment validation schema based on IPayment interface
 */
export const paymentValidationSchema = z.object({
  payment: z.object({
    paymentType: z.enum(["CC", "ACH"], {
      required_error: "paymentType is required",
      invalid_type_error: "paymentType must be either 'CC' or 'ACH'"
    }),
    // Credit card fields
    ccNumber: z.string().optional()
      .refine(
        (val) => !val || /^\d{13,19}$/.test(val),
        { message: "Credit card number must be between 13 and 19 digits" }
      ),
    ccExpMonth: z.string().optional()
      .refine(
        (val) => !val || /^(0[1-9]|1[0-2])$/.test(val),
        { message: "Credit card expiration month must be between 01 and 12" }
      ),
    ccExpYear: z.string().optional()
      .refine(
        (val) => !val || /^\d{2}$/.test(val),
        { message: "Credit card expiration year must be a 2-digit number" }
      ),
    ccSecurityCode: z.string().optional()
      .refine(
        (val) => !val || /^\d{3,4}$/.test(val),
        { message: "Credit card security code must be 3 or 4 digits" }
      ),
    // ACH fields
    achAccount: z.string().optional()
      .refine(
        (val) => !val || /^\d{5,17}$/.test(val),
        { message: "ACH account number must be between 5 and 17 digits" }
      ),
    achRouting: z.string().optional()
      .refine(
        (val) => !val || /^\d{9}$/.test(val),
        { message: "ACH routing number must be 9 digits" }
      ),
    achType: z.enum(["C", "S"]).optional(),
    achBankName: z.string().optional()
  }).refine(
    (payment) => {
      if (payment.paymentType === "CC") {
        return payment.ccNumber && payment.ccExpMonth && payment.ccExpYear && payment.ccSecurityCode;
      } else if (payment.paymentType === "ACH") {
        return payment.achAccount && payment.achRouting && payment.achType;
      }
      return false;
    },
    { message: "Missing required payment fields for the selected payment type" }
  )
});

export const buildCreditFormSchema = (cardNumberLimit: number, ccvLimit: number) => {
  return (z.object({
    cardNumber: z
      .string()
      .length(cardNumberLimit, {
        message: `Card number must be at least ${cardNumberLimit} digits long!`,
      })
      .refine((value) => /[0-9]/.test(value), "Must only be numbers"),
    month: z
      .string()
      .length(2, { message: "Month must be 2 digits long!" })
      .refine((value) => /[0-9]/.test(value), "Must only be numbers"),
    year: z
      .string()
      .length(2, { message: "Year must be 2 digits long!" })
      .refine((value) => /[0-9]/.test(value), "Must only be numbers"),
    ccv: z
      .string()
      .length(ccvLimit, { message: `CCV must be ${ccvLimit} digits long!` })
      .refine((value) => /[0-9]/.test(value), "Must only be numbers"),
  }))
};

export const buildACHFormSchema = () => {
  return z.object({
    routingNumber: z
      .string()
      .length(9, { message: "Routing number must be 9 digits long" })
      .refine((value) => /[0-9]/.test(value), "Must only be numbers"),
    accountNumber: z
      .string()
      .min(5, { message: "Account number must be between 5 and 17 digits long" })
      .max(17, { message: "Account number must be between 5 and 17 digits long" })
      .refine((value) => /[0-9]/.test(value), "Must only be numbers"),
    accountType: z.enum(["C", "S"], {
      errorMap: () => ({ message: "Please select an Account Type" }),
    }),
  });
};